import {computed} from 'vue'
import {useI18n} from 'vue-i18n'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useTimeFormat} from '@toolify/client/src/modules/date/composables/useTimeFormat'
import {DateFormatType} from '@toolify/server/src/services/UserSettingsService/strategies/DateFormatUserSettingStrategy/enum/DateFormatType'
import {format, isToday, isYesterday, isThisWeek, differenceInCalendarDays} from 'date-fns'
import {pl} from 'date-fns/locale'
import type {Locale} from 'date-fns'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'

export function useDateFormat() {
  const authStore = useAuthStore()
  const workspaceStore = useWorkspaceStore()
  const {locale: i18nLocale, t} = useI18n()
  const {clockTimeFormat} = useTimeFormat()

  const dateFnsLocale = computed<Locale | undefined>(() => {
    switch (i18nLocale.value) {
    case 'pl':
      return pl
    default:
      return undefined
    }
  })

  const currentDateFormat = computed(() => {
    const user = authStore.currentEntity?.entityType === EntityType.User
      ? authStore.currentEntity.payload as IUserResponseInstance
      : null
    const userFormat = user?.settings?.dateFormat
    if (userFormat && userFormat !== DateFormatType.Workspace) {
      return userFormat
    }
    return workspaceStore.currentWorkspace?.dateFormat || DateFormatType.Default
  })

  const formatRelativeTime = (date: Date): string => {
    const diffMs = Math.max(0, Date.now() - date.getTime())
    const minutes = Math.round(diffMs / 60_000)
    if (minutes < 1) {
      return t('general.relativeTime.justNow')
    }
    if (minutes < 60) {
      return t('general.relativeTime.minutesAgo', {n: minutes})
    }
    const hours = Math.round(minutes / 60)
    if (hours < 24) {
      return t('general.relativeTime.hoursAgo', {n: hours})
    }
    const days = Math.round(hours / 24)
    return t('general.relativeTime.daysAgo', {n: days})
  }

  const formatDisplayDate = (date: Date): string => {
    if (isToday(date)) {
      return t('general.today') + ' ' + format(date, 'p', {locale: dateFnsLocale.value})
    }

    if (isYesterday(date)) {
      return t('general.yesterday') + ' ' + format(date, 'p', {locale: dateFnsLocale.value})
    }

    if (isThisWeek(date)) {
      return format(date, 'EEEE p', {locale: dateFnsLocale.value})
    }

    return format(date, currentDateFormat.value + ' p', {locale: dateFnsLocale.value})
  }

  const formatListTimestamp = (date: Date): string => {
    if (isToday(date)) {
      return format(date, clockTimeFormat.value, {locale: dateFnsLocale.value})
    }
    if (isYesterday(date)) {
      return t('general.yesterday')
    }
    if (differenceInCalendarDays(new Date(), date) < 7) {
      return format(date, 'EEEE', {locale: dateFnsLocale.value})
    }
    return format(date, currentDateFormat.value, {locale: dateFnsLocale.value})
  }

  return {
    currentDateFormat,
    dateFnsLocale,
    formatDisplayDate,
    formatListTimestamp,
    formatRelativeTime,
  }
}
